/**
 * Facade — typed API over a dataSource.
 *
 * With `dtype="obj"` variant subclasses collapsed into
 * `DataSourceRow`, the typed per-shape APIs (e.g. "give me the
 * events on this date") live in facades obtained via
 * `row.as(FACADE_KEY)`. A facade can be backed by an index for
 * queries, or stateless — reading straight from cells /
 * attributes. Facades can attach to any `SourceRow`, so a
 * FormulaDataSource synthesizing events-shaped cells is eligible for
 * EventsFacade without any per-variant special-casing.
 *
 * The facade registry matches facades against rows at row
 * construction: `attachTo(row)` tries each registered factory
 * and installs the facades it produces. Call sites then resolve
 * typed access via `row.as(FACADE_KEY)` — null if no factory
 * matched.
 */

import type { Option } from "../core/option.ts";

/**
 * Branded key carrying the facade's public shape at the type
 * level. `row.as(EVENTS_FACADE)` returns `Option<EventsFacade>`.
 */
export class FacadeKey<F> {
  /** A key is process-global (one per facade, shared by every sheet's registry), so it is frozen. */
  constructor(readonly id: string) {
    Object.freeze(this);
  }
  /** Phantom reference to the facade type — erased at runtime. */
  declare readonly __facade: F;
}

/** Builds a typed facade over a source row for its `FacadeKey`, which `row.as(key)` resolves. */
export interface FacadeFactory<F> {
  readonly key: FacadeKey<F>;
  /**
   * Build a facade for `row` when the row is a viable match.
   * Returns `null` if the row's shape / attributes / descriptor
   * don't satisfy the facade's prerequisites.
   *
   * `row` is the `SourceRow` the facade is asked on (`row.as(key)` passes
   * itself); an implementation declares `build(row: SourceRow)`. The
   * contract names it `object` so this module does not load the row
   * classes: naming `SourceRow` kept it in the rows' import cycle.
   */
  build(row: object): Option<F>;
}

/**
 * Per-sheet facade factory registry. Populated during sheet
 * construction (via `registerBuiltinIndicesAndFacades`). Lookups
 * are by facade key; rows resolve facades lazily via `row.as(KEY)`
 * — the registry doesn't iterate eagerly at row construction.
 */
export class FacadeRegistry {
  readonly #factories: Map<string, FacadeFactory<unknown>> = new Map();

  register<F>(factory: FacadeFactory<F>): void {
    if (this.#factories.has(factory.key.id)) {
      throw new Error(
        `FacadeRegistry: facade "${factory.key.id}" is already registered`,
      );
    }
    this.#factories.set(factory.key.id, factory as FacadeFactory<unknown>);
  }

  /**
   * Look up the factory registered for `key`. Returns null when
   * no factory has been registered for this id.
   */
  get<F>(key: FacadeKey<F>): Option<FacadeFactory<F>> {
    return (this.#factories.get(key.id) ?? null) as Option<FacadeFactory<F>>;
  }
}
